import React, { Component } from 'react';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import ExportableTable from 'containers/tables/ExportableTable';
import 'react-table-6/react-table.css';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import RenderingTile from 'components/molecules/RenderingTile';
import helpers from 'helpers';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { generateFooterData, generateHeaders } from 'helpers/tables';
import { insertBeforeItem } from 'helpers/utilities';

const DEFAULT_ACCESSORS = [
    'impressions', 'conversions', 'clicks', 'ctr', 'cpc'
];
const DEFAULT_VIDEO_VIEWS_ACCESSORS = [
    'impressions', 'videoViews', 'videoViewRate', 'cpvv', 'conversions', 'clicks', 'ctr', 'cpc'
];

class YoutubePivotTable extends Component {
    constructor(props) {
        super(props);

        let columns;
        let data;
        let uniqueCampaigns;

        if (props.hasData) {
            const table = YoutubePivotTable.createTableData(props.data, props);
            columns = table.columns;
            data = table.data;

            uniqueCampaigns = new Set(data.map(c => c.campaign_name)).size;
        }

        this.state = {
            data,
            columns,
            uniqueCampaigns,
            hasData: props.hasData,
            exportName: props.exportName
        };
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps.data, this.props.data);
    }

    static createTableData(data, props) {
        const headers = generateHeaders({
            keys: [
                'impressions', 'videoViews', 'videoViewRate', 'cpvv',
                'conversions', 'conversion_revenue', 'clicks', 'ctr', 'cpc',
            ],
            colOptions: {
                labelCode: props.entityObjectiveLabelCode
            },
            footerData: generateFooterData({
                data,
                filteredKeys: ['adgroup_name', 'campaign_name']
            }),
            initial: {
                campaignName: {
                    Header: 'CAMPAIGN',
                    accessor: 'campaign_name',
                    minWidth: 250,
                    Cell: row => <CellDiv value={row.value} />,
                    PivotValue: ({ value }) => <span>{value}</span>,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                adgroupName: {
                    Header: 'AD GROUP',
                    accessor: 'adgroup_name',
                    minWidth: 250,
                    Cell: row => <CellDiv value={row.value} style={{ textAlign: 'right' }} />,
                    Aggregated: (row) => {
                        const count = row.subRows.length;
                        let value = `${count} Ad Groups`;
                        if (count === 1) {
                            value = row.subRows[0].adgroup_name;
                        } else if (row.isExpanded) {
                            value = '';
                        }
                        return (
                            <CellDiv
                                strong={count !== 1}
                                value={value}
                                style={{ textAlign: 'right' }}
                            />
                        );
                    },
                    Footer: (
                        <CellDiv value=' ' strong />
                    )
                }
            }
        });

        let defaultAccessors;
        switch (props.tableType) {
        case 'youtubeVideoViews':
            defaultAccessors = [].concat('campaignName', 'adgroupName', DEFAULT_VIDEO_VIEWS_ACCESSORS);
            break;
        case 'youtube':
        default:
            defaultAccessors = [].concat('campaignName', 'adgroupName', DEFAULT_ACCESSORS);
        }

        let { accessors = defaultAccessors } = props;

        if (props.showConversionRevenue && !accessors.includes('conversion_revenue')) {
            accessors = insertBeforeItem(accessors, 'clicks', 'conversion_revenue');
        }
        const columns = accessors.map(accessor => headers[accessor]);

        return { data, columns };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextData = nextProps.data;
        const nextExportName = nextProps.exportName;
        const nextHasData = nextProps.hasData;
        let nextState = null;

        if (!isEqual(nextData, prevState.data)) {
            let columns;
            let data;
            let uniqueCampaigns;

            if (nextHasData) {
                const table = YoutubePivotTable.createTableData(nextData, nextProps);
                columns = table.columns;
                data = table.data;

                uniqueCampaigns = new Set(data.map(c => c.creativeLabel)).size;
            }

            nextState = {
                ...prevState,
                columns,
                data,
                exportName: nextExportName,
                hasData: nextHasData,
                uniqueCampaigns
            };
        }
        return nextState;
    }

    render() {
        const { data, exportName, columns, hasData, uniqueCampaigns } = this.state;

        const expanded = uniqueCampaigns > 1 ? undefined : { 0: true };

        return hasData ? (
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'}>PERFORMANCE OVERVIEW</ContentHeader>
                <ExportableTable
                    key={1}
                    title="PERFORMANCE OVERVIEW"
                    exportName={exportName}
                    data={data}
                    columns={columns}
                    expanded={expanded}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    getTfootProps= {() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    pivotBy={['campaign_name']}
                    defaultSorted={[
                        { id: 'campaign_name', desc: false },
                        { id: 'adgroup_name', desc: false }
                    ]}
                    showPagination={uniqueCampaigns > RowsPerPageDefault}
                    PaginationComponent={Pagination}
                    defaultPageSize={RowsPerPageDefault}
                    exportRows={data.length}
                    minRows={uniqueCampaigns < RowsPerPageDefault ? uniqueCampaigns : RowsPerPageDefault}
                    className="-highlight"
                />
            </ContentTile>
        ) : (
            <RenderingTile height='400px' />
        );
    }
}

YoutubePivotTable.propTypes = {
    exportName: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
        campaign_name: PropTypes.string,
        adgroup_name: PropTypes.string
    })),
    accessors: PropTypes.arrayOf(PropTypes.string),
    showConversionRevenue: PropTypes.bool,
    hasData: PropTypes.bool.isRequired,
    tableType: PropTypes.string,
};

export default YoutubePivotTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/YoutubePivotTable.js